import React, { ReactElement } from "react";
import {
  AcceptatieHoofdstuk,
  AcceptatieHypotheek,
} from "../../../../.generated/rapportage/rapportagetypes";
import Heading from "../../components/Heading";
import { Paragraph } from "../../components/Paragraph";
import { Chapter } from "../../components/Chapter";
import Table from "../../components/Table";
import { Column } from "../../components/BaseTable";
import { formatPercentageFractionDigits } from "../../../formatters";

type AcceptatieProps = {
  acceptatie: AcceptatieHoofdstuk;
};

export const AcceptatieChapter = ({
  acceptatie,
}: AcceptatieProps): ReactElement => {
  const aanvragersColumns: Column[] = [
    {
      header: "",
      accessor: "title",
    },
    {
      header: "Naam",
      accessor: "naam",
    },
    {
      header: "Geboortedatum",
      accessor: "geboortedatum",
      dataType: "date",
    },
    {
      header: "Geslacht",
      accessor: "geslacht",
    },
    {
      header: "Bruto jaarinkomen",
      accessor: "brutoJaarInkomen",
      dataType: "currency",
    },
  ];

  const pandColumns: Column[] = [
    {
      header: "Pand",
      accessor: () => "",
    },
    {
      header: "Marktwaarde",
      accessor: "marktwaardeBedrag",
      dataType: "currency",
    },
  ];

  const hypotheekColumns: Column[] = [
    {
      header: "Hypotheek",
      accessor: () => "",
    },
    {
      header: "Geldverstrekker",
      accessor: "geldverstrekker",
    },
    {
      header: "Gewenste hypotheek",
      accessor: "gewensteHypotheek",
      dataType: "currency",
    },
    {
      header: "Toetsrente",
      accessor: (row: AcceptatieHypotheek) =>
        formatPercentageFractionDigits(row.toetsrente, 3, 3),
    },
    {
      header: "Woonquote",
      accessor: (row: AcceptatieHypotheek) =>
        formatPercentageFractionDigits(row.woonQuote, 3, 3),
    },
    {
      header: "Fictieve jaarlast",
      accessor: "fictieveJaarlast",
      dataType: "currency",
    },
    {
      header: "Toegestane jaarlast",
      accessor: "toegestaneJaarlast",
      dataType: "currency",
    },
    {
      header: "Indicatie meer/ minder lenen",
      accessor: "indicatieMeerMinderLenen",
      dataType: "currency",
    },
    {
      header: "Maximale hypotheek o.b.v. inkomen en lasten",
      accessor: "maximaleHypotheekOpBasisVanInkomenEnLasten",
      dataType: "currency",
    },
    {
      header: "Maximale hypotheek o.b.v. onderpand en lening",
      accessor: "maximaleHypotheekOpBasisVanOnderpandEnLening",
      dataType: "currency",
    },
    {
      header: "Maximale hypotheek o.b.v. NHG voorwaarden",
      accessor: "maximaleHypotheekOpBasisVanNhgVoorwaarden",
      dataType: "currency",
    },
    {
      header: "Verplichte extra aflossing",
      accessor: "verplichteExtraAflossing",
      dataType: "currency",
    },
    {
      header: "Looptijd extra aflossing",
      accessor: (row: any) =>
        row.looptijdExtraAflossing
          ? `${row.looptijdExtraAflossing} maanden`
          : null,
    },
  ];

  return (
    <Chapter>
      <Heading text="Overzicht hypotheekacceptatie en toetsing" level={2} />

      <Paragraph>
        Indicatieve berekening van de maximale leencapaciteit op basis van de
        bij maatschappij bekende gegevens
      </Paragraph>

      <div className="acceptatie-content">
        {/* Aanvragers sectie */}
        <div className="acceptatie-section">
          <Table
            columns={aanvragersColumns}
            data={[
              { ...acceptatie.aanvrager1, title: "Aanvrager 1" },
              { ...acceptatie.aanvrager2, title: "Aanvrager 2" },
            ]}
            id="acceptatie-aanvragers"
            filterNullAnd0Columns={false}
            rotate
          />
        </div>

        {/* Pand sectie */}
        {acceptatie.hypotheek && (
          <div className="acceptatie-section">
            <Table
              columns={pandColumns}
              data={[acceptatie.hypotheek]}
              id="acceptatie-pand"
              filterNullAnd0Columns={true}
              rotate
            />
          </div>
        )}

        {/* Hypotheek sectie */}
        {acceptatie.hypotheek && (
          <div className="acceptatie-section">
            {/* Algemene hypotheek info */}
            <Table
              columns={hypotheekColumns}
              data={[acceptatie.hypotheek]}
              id="acceptatie-hypotheek-general"
              filterNullAnd0Columns={true}
              rotate
            />
          </div>
        )}

        {/* Indicatie sectie */}
        {acceptatie.indicatie && (
          <div className="acceptatie-section">
            <Table
              columns={[
                {
                  header: "Indicatie",
                  accessor: (row: any) =>
                    row.indicatie ? String(row.indicatie).toUpperCase() : "",
                },
              ]}
              data={[acceptatie.indicatie]}
              id="acceptatie-indicatie"
              filterNullAnd0Columns={false}
              rotate
            />

            {/* Toelichting regels */}
            {acceptatie.indicatie.toelichtingRegels &&
              acceptatie.indicatie.toelichtingRegels.length > 0 && (
                <div className="acceptatie-toelichting">
                  <p>
                    <strong>Toelichting</strong>
                  </p>
                  <ul>
                    {acceptatie.indicatie.toelichtingRegels.map(
                      (regel, index) => (
                        <li key={index}>{regel}</li>
                      )
                    )}
                  </ul>
                </div>
              )}

            {/* Voetnoten */}
            {acceptatie.indicatie.voetnoten &&
              acceptatie.indicatie.voetnoten.length > 0 && (
                <div className="acceptatie-voetnoten">
                  {acceptatie.indicatie.voetnoten.map((voetnoot, index) => (
                    <p key={index} className="acceptatie-voetnoot">
                      * {voetnoot}
                    </p>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>
    </Chapter>
  );
};
